import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { useAuthStatus } from '../auth/useAuthStatus';
import { BrandConfig, BrandContext, useBrand } from './config/brand';
import { Icons } from '../shared/icons/Icons';

// Layouts
import { MainLayout } from '../shared/layouts/MainLayout';
import AdminLayout from '../shared/layouts/AdminLayout';
import { BareLayout } from '../shared/layouts/BareLayout';
import { AdminCompanyProvider } from '../shared/contexts/AdminCompanyContext';
import { SessionExpiryProvider } from '../shared/contexts/SessionExpiryContext';
import { ReauthRequiredProvider } from '../shared/contexts/ReauthRequiredContext';

// Shared
import LoadingPage from '../shared/components/LoadingPage';
import SessionExpiryModal from '../shared/components/SessionExpiryModal';
import ReauthPrompt from '../shared/components/ReauthPrompt';
import { IdentityRefusedProvider } from '../shared/contexts/IdentityRefusedContext';
import IdentityRefusedBanner from '../shared/components/IdentityRefusedBanner';
import { SkipLink } from '../shared/components/SkipLink';

// Feature pages
import CustomerDashboard from '../features/dashboard/pages/CustomerDashboard';
import CustomerOrders from '../features/orders/pages/CustomerOrders';
import CustomerDrafts from '../features/orders/pages/CustomerDrafts';
import CustomerPendingSubmission from '../features/orders/pages/CustomerPendingSubmission';
import CustomerCases from '../features/cases/CustomerCases';
import CaseDetailPage from '../features/cases/CaseDetailPage';
import CustomerMatters from '../features/orders/pages/CustomerMatters';
import AttorneyRosterPage from '../features/attorneys/pages/AttorneyRosterPage';
import CustomerCourtFiling from '../features/efiling/pages/CustomerCourtFiling';
import CustomerServiceOfProcess from '../features/efiling/pages/CustomerServiceOfProcess';
import CustomerProofread from '../features/efiling/pages/CustomerProofread';
import CustomerEFileEServe from '../features/efiling/pages/CustomerEFileEServe';
import CustomerProfile from '../features/profile/pages/CustomerProfile';
import CustomerSettings from '../features/profile/pages/CustomerSettings';
import PaymentMethodsPage from '../features/payments/pages/PaymentMethodsPage';
import IntegrationsPage from '../features/integrations/pages/IntegrationsPage';
import LoginPage from '../features/registration/pages/LoginPage';
import RegistrationPage from '../features/registration/pages/RegistrationPage';
import AdminRegistrationPage from '../features/registration/pages/AdminRegistrationPage';
import OnboardingPage from '../features/onboarding/pages/OnboardingPage';
import PendingApprovalPage from '../features/onboarding/pages/PendingApprovalPage';
import LegalDocumentPage from '../features/legal/pages/LegalDocumentPage';
import LegalUpcomingDocumentPage from '../features/legal/pages/LegalUpcomingDocumentPage';
import { TermsNotice } from '../features/legal/TermsNotice';
import EFilingWizard from '../features/efiling/components/EFilingWizard';
import NewOrderPage from '../features/orders/pages/NewOrderPage';
import OrderDetailPage from '../features/orders/pages/OrderDetailPage';
import OrderConfirmation from '../features/orders/pages/OrderConfirmation';


// Page wrappers that inject React Router navigation
const DashboardPageWrapper = () => {
  const navigate = useNavigate();
  return <CustomerDashboard onNavigate={(page) => navigate(`/${page}`)} />;
};

const EFilingPageWrapper = () => {
  const brand = useBrand();
  return <EFilingWizard brand={brand} />;
};

const CourtFilingPageWrapper = () => {
  const navigate = useNavigate();
  return <CustomerCourtFiling onNavigate={(page) => navigate(`/${page}`)} />;
};

const ServiceOfProcessPageWrapper = () => {
  const navigate = useNavigate();
  return <CustomerServiceOfProcess onNavigate={(page) => navigate(`/${page}`)} />;
};

const LoginPageWrapper = ({ onLogin }: { onLogin: () => void }) => {
  const navigate = useNavigate();
  return <LoginPage onLogin={onLogin} onNavigate={(page) => navigate(`/${page}`)} />;
};

const RegistrationPageWrapper = ({ onSignup }: { onSignup: () => void }) => {
  const navigate = useNavigate();
  return <RegistrationPage onNavigate={(page) => navigate(`/${page}`)} onSignup={onSignup} />;
};

const AdminRegistrationPageWrapper = ({ onSignup }: { onSignup: () => void }) => {
  const navigate = useNavigate();
  return <AdminRegistrationPage onNavigate={(page) => navigate(`/${page}`)} onSignup={onSignup} />;
};

// Protected route wrapper
export const ProtectedRoute = ({ children, isLoading, isAuthenticated, onboardingCompleted, canAccessApp, noticeSurfaceEnabled, user, handleOnboardingComplete, logout }: {
  children: React.ReactNode;
  isLoading: boolean;
  isAuthenticated: boolean;
  onboardingCompleted: boolean;
  canAccessApp: boolean;
  // Customer-only switch: whether the notice surface mounts at all. A platform
  // user (operator or admin) is never shown a notice and never consults the
  // endpoint — the decision is made once, at the mount site.
  noticeSurfaceEnabled: boolean;
  user: { name?: string; email?: string } | undefined;
  handleOnboardingComplete: () => void;
  logout: () => void;
}) => {
  const location = useLocation();

  if (isLoading) return <LoadingPage />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!onboardingCompleted) return <BareLayout><OnboardingPage user={user} onComplete={handleOnboardingComplete} onLogout={logout} /></BareLayout>;

  // The Terms notice (SOR-421) is mounted UNCONDITIONALLY for every signed-in
  // customer — not behind the verdict, not behind the read succeeding, and not
  // inside the page it renders beside: it is the guard's own sibling of
  // `children`, so no route's content and no wizard step can unmount it. The
  // guard itself IS each customer route's element (`protectedPage` wraps every
  // one), so navigating between routes does remount this surface — which is
  // why the focus claim sits at module scope in `useTermsNotice` rather than in
  // a per-mount ref. The account-class switch alone decides whether it mounts:
  // no legal-document version state reaches this arm, so nothing the notice is
  // about can refuse the route beside it.
  const legalNotice = noticeSurfaceEnabled ? <TermsNotice /> : null;

  // The approval arm is the guard's remaining refusal: an onboarded-but-
  // unapproved customer sees its page in place of the route's content, with the
  // notice still beside it rather than standing where the page would be.
  const guarded = !canAccessApp ? <BareLayout><PendingApprovalPage user={user} onLogout={logout} /></BareLayout> : children;

  return <>{legalNotice}{guarded}</>;
};

const App: React.FC = () => {
  const { isLoading, isAuthenticated, canAccessApp, isPlatformUser, platformRole, onboardingCompleted, user, login, signup, logout } = useAuthStatus();
  const location = useLocation();

  const handleOnboardingComplete = () => { login(); };

  // The auth-loading screen renders before any route resolves — not a route,
  // so R3's heading half doesn't bind to it — but the skip link still renders
  // here with its BareLayout target, so the anchor never points at nothing.
  // The ErrorBoundary crash fallback (main.tsx) sits outside the router
  // entirely and is deliberately out of scope: not a route, own h1.
  if (isLoading) {
    return (
      <BrandContext value={BrandConfig}>
        <SkipLink />
        <BareLayout>
          <LoadingPage />
        </BareLayout>
      </BrandContext>
    );
  }

  // Shared props for ProtectedRoute. `noticeSurfaceEnabled` is keyed on
  // `!isPlatformUser` — the one producer that decides who counts as a customer:
  // a platform user (operator or admin) is never shown the notice and never
  // consults the endpoint.
  const protectedProps = { isLoading, isAuthenticated, onboardingCompleted, canAccessApp, noticeSurfaceEnabled: !isPlatformUser, user, handleOnboardingComplete, logout };

  // Helper to wrap a page in ProtectedRoute + MainLayout. The notice surface
  // mounts inside the guard (SOR-421) — for customers only, so onboarding,
  // pending-approval, and every /admin/* operator surface stay reachable and
  // platform users are never shown a notice.
  const protectedPage = (page: React.ReactNode) => (
    <ProtectedRoute {...protectedProps}>
      <MainLayout onLogout={logout} user={user}>
        {page}
      </MainLayout>
    </ProtectedRoute>
  );

  // Helper to wrap a public page in BareLayout — the routes that render outside
  // both layouts still need their one `<main>` landmark (and skip target).
  const publicPage = (page: React.ReactNode) => (
    <BareLayout>
      {page}
    </BareLayout>
  );

  return (
    <BrandContext value={BrandConfig}>
      <SkipLink />
      <SessionExpiryProvider>
      <ReauthRequiredProvider>
      <IdentityRefusedProvider>
      <SessionExpiryModal />
      <ReauthPrompt />
      <IdentityRefusedBanner />
      <AdminCompanyProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : publicPage(<LoginPageWrapper onLogin={login} />)} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : publicPage(<RegistrationPageWrapper onSignup={signup} />)} />
        <Route path="/admin-register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : publicPage(<AdminRegistrationPageWrapper onSignup={signup} />)} />
        <Route path="/legal/terms" element={publicPage(<LegalDocumentPage documentId="terms" />)} />
        <Route path="/legal/privacy" element={publicPage(<LegalDocumentPage documentId="privacy" />)} />
        <Route path="/legal/accessibility" element={publicPage(<LegalDocumentPage documentId="accessibility" />)} />
        <Route path="/legal/terms/upcoming" element={publicPage(<LegalUpcomingDocumentPage documentId="terms" />)} />
        <Route path="/legal/privacy/upcoming" element={publicPage(<LegalUpcomingDocumentPage documentId="privacy" />)} />
        <Route path="/legal/accessibility/upcoming" element={publicPage(<LegalUpcomingDocumentPage documentId="accessibility" />)} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          isAuthenticated && isPlatformUser
            ? <Navigate to="/admin/dashboard" replace />
            : protectedPage(<DashboardPageWrapper />)
        } />
        <Route path="/drafts" element={protectedPage(<CustomerDrafts />)} />
        <Route path="/pending" element={protectedPage(<CustomerPendingSubmission />)} />
        <Route path="/orders" element={protectedPage(<CustomerOrders />)} />
        <Route path="/orders/new" element={protectedPage(<NewOrderPage />)} />
        <Route path="/orders/new/:type" element={protectedPage(<NewOrderPage />)} />
        <Route path="/orders/:id" element={protectedPage(<OrderDetailPage />)} />
        <Route path="/orders/:id/confirmation" element={protectedPage(<OrderConfirmation />)} />
        <Route path="/orders/:id/edit" element={protectedPage(<NewOrderPage />)} />
        <Route path="/cases" element={protectedPage(<CustomerCases />)} />
        <Route path="/cases/:caseNumber" element={protectedPage(<CaseDetailPage />)} />
        <Route path="/matters" element={protectedPage(<CustomerMatters />)} />
        <Route path="/attorneys" element={protectedPage(<AttorneyRosterPage />)} />
        <Route path="/profile" element={protectedPage(<CustomerProfile />)} />
        <Route path="/eFiling" element={protectedPage(<EFilingPageWrapper />)} />
        <Route path="/courtFiling" element={protectedPage(<CourtFilingPageWrapper />)} />
        <Route path="/serviceOfProcess" element={protectedPage(<ServiceOfProcessPageWrapper />)} />
        <Route path="/proofread" element={protectedPage(<CustomerProofread />)} />
        <Route path="/efileEserve" element={protectedPage(<CustomerEFileEServe />)} />
        <Route path="/eService" element={protectedPage(<CourtFilingPageWrapper />)} />
        <Route path="/settings" element={protectedPage(<CustomerSettings />)} />
        <Route path="/settings/payment-methods" element={protectedPage(<PaymentMethodsPage />)} />
        <Route path="/settings/integrations" element={protectedPage(<IntegrationsPage />)} />

        {/* Admin Routes */}
        <Route path="/admin/*" element={
          !isAuthenticated
            ? <Navigate to="/login" state={{ returnTo: location.pathname }} replace />
            : !isPlatformUser
              ? <Navigate to="/dashboard" replace />
              : <AdminLayout brand={BrandConfig} Icons={Icons} user={user} onLogout={logout} platformRole={platformRole} />
        } />

        {/* Redirects */}
        <Route path="/" element={isAuthenticated && isPlatformUser ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/dashboard" replace />} />
        <Route path="*" element={isAuthenticated && isPlatformUser ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/dashboard" replace />} />
      </Routes>
      </AdminCompanyProvider>
      </IdentityRefusedProvider>
      </ReauthRequiredProvider>
      </SessionExpiryProvider>
    </BrandContext>
  );
};

export default App;
